import { useState, useEffect } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { CalendarIcon } from "lucide-react";
import { tenantLocale, tenantTz } from "../lib/company";
import { calendarDateToYmd, toTenantWall, wallToCalendarDate, wallToDate } from "../lib/tenantTime";

// Date → 'YYYY-MM-DDTHH:MM' wall-clock string in the TENANT's timezone
// (same wire shape as <input type="datetime-local">).
export const toLocalInput = (d) => toTenantWall(d);

// Format the wire 'YYYY-MM-DDTHH:MM' as a friendly 24h preview.
export function previewLabel(s) {
  if (!s) return "Pick a date & time";
  return wallToDate(s).toLocaleString(tenantLocale(), {
    timeZone: tenantTz(),
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

// Calendar day change keeps the HH:mm; time change keeps the date.
export const withDate = (value, d) =>
  `${calendarDateToYmd(d)}T${(value || toTenantWall()).slice(11, 16)}`;
export const withTime = (value, t) => `${(value || toTenantWall()).slice(0, 10)}T${t}`;

// Settings that store a bare hour (0-23) but must READ as a time: 17 → "17:00".
export const hhmm = (h) => `${String(Number(h) || 0).padStart(2, "0")}:00`;
export const hourOf = (s) => Number(String(s || "").split(":")[0]) || 0;

/**
 * 24-hour time text input — replaces <input type="time">, which follows the
 * device's am/pm regional setting and can't be forced to 24h from the web.
 * Accepts "1730" or "17:30"; normalises on blur. Buffers keystrokes locally
 * so the field stays responsive even when a parent guards on strict HH:mm.
 * Widened + taller for easier tapping on tablets and touch laptops.
 */
export function TimeInput24({ value, onChange, className = "", disabled, dataTestId, placeholder = "HH:mm" }) {
  const [buf, setBuf] = useState(value || "");
  useEffect(() => { setBuf(value || ""); }, [value]);

  const handle = (raw) => {
    let v = raw.replace(/[^0-9:]/g, "");
    if (/^\d{4}$/.test(v)) v = v.slice(0, 2) + ":" + v.slice(2);
    if (v.length > 5) v = v.slice(0, 5);
    setBuf(v);
    const m = /^(\d{2}):(\d{2})$/.exec(v);
    if (m) {
      const h = parseInt(m[1], 10);
      const mm = parseInt(m[2], 10);
      if (h <= 23 && mm <= 59) onChange(v);
    }
  };
  const commit = () => {
    if (!buf) { if (value) onChange(""); return; }
    const m = /^(\d{1,2}):?(\d{2})?$/.exec(buf);
    if (!m || m[2] == null) { setBuf(value || ""); return; }
    const h = Math.min(23, parseInt(m[1], 10));
    const mm = Math.min(59, parseInt(m[2], 10));
    const norm = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    setBuf(norm);
    if (norm !== value) onChange(norm);
  };
  return (
    <Input
      type="text"
      inputMode="numeric"
      placeholder={placeholder}
      maxLength={5}
      value={buf}
      onChange={(e) => handle(e.target.value)}
      onBlur={commit}
      // Select the whole "HH:mm" on focus/click so a dispatcher can just
      // tap and type 4 digits — no manual delete/retype cycle.
      onFocus={(e) => e.target.select()}
      onMouseUp={(e) => e.preventDefault()}
      // Bigger tap target than the previous w-24: wider and taller by default.
      className={`w-28 h-11 text-center font-mono text-base tracking-wide ${className}`}
      disabled={disabled}
      data-testid={dataTestId}
      title="24-hour time, e.g. 17:30"
    />
  );
}

const QUICK_OPTIONS = [
  { label: "Now", mins: 0 },
  { label: "+15m", mins: 15 },
  { label: "+30m", mins: 30 },
  { label: "+1h", mins: 60 },
  { label: "+2h", mins: 120 },
];

/**
 * Date + 24h time picker with a single big tap target.
 * Same "YYYY-MM-DDTHH:mm" wire format as <input type="datetime-local">.
 *
 * Opens a popover with a month calendar, a large 24h time box, and quick
 * "Now / +15m / +30m / +1h / +2h" chips — the same UX as the Assign Truck
 * scheduler, unified across all datetime fields (impound intake, edit
 * impound, AA tow pricing, "book for later" jobs, …).
 */
export function DateTime24({ value, onChange, disabled, dataTestId }) {
  const [open, setOpen] = useState(false);
  const dateSelected = wallToCalendarDate(value);
  const timePart = (value || "").slice(11, 16);

  const setNow = () => { onChange(toLocalInput(new Date())); };
  const bumpBy = (mins) => { onChange(toLocalInput(new Date(Date.now() + mins * 60 * 1000))); };
  const clear = () => { onChange(""); };

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-start text-left font-normal text-base h-11"
          data-testid={dataTestId}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {previewLabel(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        collisionPadding={16}
        data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
      >
        <Calendar
          mode="single"
          selected={dateSelected}
          onSelect={(d) => {
            if (!d) return;
            onChange(withDate(value, d));
          }}
          initialFocus
        />
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 flex items-center gap-2">
          <Label className="uppercase-label !text-xs">Time (24h)</Label>
          <TimeInput24
            value={timePart}
            onChange={(t) => {
              if (!/^\d{2}:\d{2}$/.test(t)) return;
              onChange(withTime(value, t));
            }}
            dataTestId={dataTestId ? `${dataTestId}-time` : undefined}
          />
        </div>
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 flex flex-wrap gap-1.5">
          {QUICK_OPTIONS.map((q) => (
            <button
              key={q.label}
              type="button"
              onClick={() => (q.mins === 0 ? setNow() : bumpBy(q.mins))}
              className="text-xs font-semibold px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-800 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-blue-900/40 dark:hover:border-blue-700 dark:hover:text-blue-200 transition-colors"
              data-testid={dataTestId ? `${dataTestId}-quick-${q.label.toLowerCase().replace('+', 'plus')}` : undefined}
            >
              {q.label}
            </button>
          ))}
          {value && (
            <button
              type="button"
              onClick={clear}
              className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-md border border-transparent text-slate-500 hover:text-red-600 hover:border-red-200 dark:hover:text-red-300 dark:hover:border-red-800 transition-colors"
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white transition-colors"
            data-testid={dataTestId ? `${dataTestId}-done` : undefined}
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
